import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { create } from '@bufbuild/protobuf'
import { timestampFromDate } from '@bufbuild/protobuf/wkt'
import { Pencil, Plus, RotateCcw, UserPlus, X } from 'lucide-react'
import { Skeleton } from 'skullet/react'
import { toast } from 'sonner'

import {
  EventAddonSchema,
  EventAddonSourceEnum,
  EventTimelineBlockSchema,
  TimelineBlockKindEnum,
  type EventAddon,
} from '@/gen/cortado/v1/event_plan_pb'
import { EventTimelineSchema, type EventTimeline } from '@/gen/cortado/v1/event_timeline_pb'
import { BookingSchema, type Booking, type Event } from '@/gen/cortado/v1/inquiries_pb'
import { JobEventStaffSchema, type JobEventStaff } from '@/gen/cortado/v1/jobs_pb'
import {
  QuestionnaireAnswerSchema,
  QuestionnaireItemSchema,
  QuestionnaireSchema,
} from '@/gen/cortado/v1/questionnaires_pb'
import { UserSchedulingRoleEnum, UserSchedulingStatusEnum } from '@/gen/cortado/v1/settings_pb'
import { getAvatarColor, getInitials } from '@/lib/avatar-utils'
import { formatQuestionnaireAnswer, sortedQuestionnaireItems } from '@/lib/booking-questionnaire'
import { getErrorDescription } from '@/lib/error-utils'
import {
  blockTimeInputValue,
  describeTimelineChanges,
  draftFromBlocks,
  draftToBlockInputs,
  editDraftBlockMinutes,
  editDraftBlockStart,
  formatBlockDuration,
  formatBlockTime,
  inlineTimelineEdits,
  isDriveKind,
  MAX_TIMELINE_BLOCK_MINUTES,
  timelineKindColor,
  withBlockTime,
  type TimelineDraftBlock,
  type TimelineEdit,
} from '@/lib/event-timeline'
import { cn, formatFullName } from '@/lib/utils'
import { usePermissions } from '@/hooks/use-permissions'
import { useEventAddonMutations } from '@/stores/event-addons.store'
import { useEventTimeline, useUpdateEventTimelineBlocks } from '@/stores/event-timeline.store'
import { useListItems } from '@/stores/organization.store'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ActionGate } from '@/components/shared/action-gate'
import { questionnaireDeliveryLabel } from '@/components/events/id/booking-questionnaire-card'

interface RunSheetSectionProps {
  /** The anchor the readiness chips and the job page link to (EVENT_SECTION_IDS). */
  id: string
  number: string
  title: string
  /** The muted line beside the title: counts, where the data came from. */
  summary?: ReactNode
  /** The header's one action, which opens a sheet; the section itself reads. */
  action?: ReactNode
  children: ReactNode
}

/** One numbered section of the run sheet: "01 Staffing", its summary, one action, and its body. */
export function RunSheetSection({ id, number, title, summary, action, children }: RunSheetSectionProps) {
  return (
    <section id={id} aria-labelledby={`${id}-title`} className="scroll-mt-4 py-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
          <span aria-hidden="true" className="font-mono text-xs font-medium tracking-widest text-brand">
            {number}
          </span>
          <h2 id={`${id}-title`} className="text-base font-semibold">
            {title}
          </h2>
          {summary && <span className="text-xs text-muted-foreground">{summary}</span>}
        </div>
        {action}
      </div>
      {children}
    </section>
  )
}

const STAFF_STATUS: Partial<Record<UserSchedulingStatusEnum, { label: string; className: string }>> = {
  [UserSchedulingStatusEnum.USER_SCHEDULING_STATUS_PENDING]: {
    label: 'Pending',
    className: 'border-status-warning-border text-status-warning-fg',
  },
  [UserSchedulingStatusEnum.USER_SCHEDULING_STATUS_ACCEPTED]: {
    label: 'Confirmed',
    className: 'border-status-success-border text-status-success-fg',
  },
  [UserSchedulingStatusEnum.USER_SCHEDULING_STATUS_REJECTED]: {
    label: 'Declined',
    className: 'border-status-danger-border text-status-danger-fg',
  },
}

const SAMPLE_STAFF: JobEventStaff[] = [
  { firstName: 'Jordan', lastName: 'Rivera', role: UserSchedulingRoleEnum.USER_SCHEDULING_ROLE_LEAD },
  { firstName: 'Sam', lastName: 'Okafor', role: UserSchedulingRoleEnum.USER_SCHEDULING_ROLE_BARISTA },
].map((person, i) =>
  create(JobEventStaffSchema, {
    ...person,
    userId: `sample-${i}`,
    status: UserSchedulingStatusEnum.USER_SCHEDULING_STATUS_ACCEPTED,
  }),
)

const SAMPLE_ADDONS: EventAddon[] = [
  { name: 'Oat milk upgrade', description: 'Bring two extra cartons' },
  { name: 'Branded cup sleeves', description: 'Pack with the cups' },
].map((addon, i) => create(EventAddonSchema, { ...addon, lineItemId: `sample-${i}`, packQuantity: 2 }))

const SAMPLE_TIMELINE: EventTimeline = create(EventTimelineSchema, {
  driveTimeKnown: true,
  blocks: [
    { kind: TimelineBlockKindEnum.TIMELINE_BLOCK_KIND_DRIVE_TO, label: 'Drive to venue', minutes: 35 },
    { kind: TimelineBlockKindEnum.TIMELINE_BLOCK_KIND_SETUP, label: 'Setup', minutes: 45 },
    { kind: TimelineBlockKindEnum.TIMELINE_BLOCK_KIND_EVENT, label: 'Service', minutes: 180 },
    { kind: TimelineBlockKindEnum.TIMELINE_BLOCK_KIND_TEARDOWN, label: 'Teardown', minutes: 45 },
  ].map((block, i) =>
    create(EventTimelineBlockSchema, { ...block, id: `sample-${i}`, start: timestampFromDate(new Date(2026, 0, 1, 12 + i)) }),
  ),
})

const SAMPLE_BOOKING: Booking = create(BookingSchema, {
  id: 'sample',
  questionnaire: create(QuestionnaireSchema, {
    items: [
      ['Where should we park?', 'Loading dock behind the hall'],
      ['Is there power near the setup area?', 'Yes, two outlets by the stage'],
    ].map(([prompt, answer], order) =>
      create(QuestionnaireItemSchema, {
        id: `sample-${order}`,
        order,
        prompt,
        answer: create(QuestionnaireAnswerSchema, { value: { case: 'text', value: answer } }),
      }),
    ),
  }),
})

function StaffPill({ person }: { person: JobEventStaff }) {
  const name = formatFullName(person.firstName, person.lastName, 'Unnamed')
  const status = STAFF_STATUS[person.status]
  const isLead = person.role === UserSchedulingRoleEnum.USER_SCHEDULING_ROLE_LEAD
  return (
    <li
      aria-label={`${name} · ${isLead ? 'Lead' : 'Barista'}${status ? ` · ${status.label}` : ''}`}
      className="inline-flex items-center gap-2 rounded-full border py-1 pr-3 pl-1 text-sm"
    >
      <span
        aria-hidden="true"
        className={cn(
          'flex size-6 items-center justify-center rounded-full text-xs font-semibold text-white',
          getAvatarColor(name),
        )}
      >
        {getInitials(person.firstName, person.lastName)}
      </span>
      <span className="font-medium">{name}</span>
      <Badge variant={isLead ? 'info' : 'outline'}>{isLead ? 'Lead' : 'Barista'}</Badge>
      {status && <span className={cn('rounded-full border px-2 text-xs', status.className)}>{status.label}</span>}
    </li>
  )
}

function plural(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`
}

interface StaffingSectionProps {
  id: string
  event: Event
  /** The event's staff from the job's event list; undefined while that list loads. */
  staff: JobEventStaff[] | undefined
  onAssign: () => void
}

/** 01 Staffing: who is on the event, Lead first, with each person's answer. Assigning happens in the sheet. */
export function StaffingSection({ id, event, staff, onAssign }: StaffingSectionProps) {
  const needed = [
    event.baristaCount != null && event.baristaCount > 0 ? `${event.baristaCount} staff` : '',
    event.cartCount != null && event.cartCount > 0 ? plural(event.cartCount, 'cart') : '',
  ]
    .filter(Boolean)
    .join(' · ')
  const hasStaff = !!staff && staff.length > 0

  return (
    <RunSheetSection
      id={id}
      number="01"
      title="Staffing"
      summary={needed ? `${needed} needed` : undefined}
      action={
        <ActionGate organizationId={event.organizationId} permission="EventsManage" verb="assign" noun="staff">
          <Button type="button" size="sm" variant={hasStaff ? 'ghost' : 'outline'} onClick={onAssign}>
            <UserPlus data-icon="inline-start" />
            {hasStaff ? 'Change staff' : 'Assign staff'}
          </Button>
        </ActionGate>
      }
    >
      <Skeleton
        data={staff}
        sample={SAMPLE_STAFF}
        label="Loading staff"
        empty={staff !== undefined && !hasStaff}
        emptyMessage={<p className="text-sm text-muted-foreground">No one assigned yet.</p>}
      >
        {(staff) => (
          <ul aria-label="Staff on this event" className="flex flex-wrap items-center gap-2">
            {staff.map((person) => (
              <StaffPill key={person.schedulingId || person.userId} person={person} />
            ))}
          </ul>
        )}
      </Skeleton>
    </RunSheetSection>
  )
}

function addonQuantity(addon: EventAddon): string {
  if (addon.packQuantity > 1) return `× ${addon.packQuantity}`
  return ''
}

/** The most of one add-on an event can have (AddEventAddonRequest). */
const MAX_ADDON_QUANTITY = 10000

interface AddonsSectionProps {
  id: string
  event: Event
  addons: EventAddon[] | undefined
  /** Quote add-ons taken off this event; the quote still has them. */
  removedAddons: EventAddon[]
  isError: boolean
}

/**
 * 02 Add-ons: what the client ticked on acceptance, with its prep note and how
 * many to pack. Add-ons can be added from the catalog or taken off for this
 * event alone; the quote and its price never change. A removed quote add-on
 * stays listed so it can be restored.
 */
export function AddonsSection({ id, event, addons, removedAddons, isError }: AddonsSectionProps) {
  const { can } = usePermissions(event.organizationId)
  const { add, remove, restore } = useEventAddonMutations(event.id)
  const canEdit = can('EventsManage')
  const isSaving = add.isPending || remove.isPending || restore.isPending
  const changed = removedAddons.length > 0 || !!addons?.some((a) => a.source === EventAddonSourceEnum.EVENT_ADDON_SOURCE_EVENT)

  const run = async (action: () => Promise<unknown>, failure: string) => {
    try {
      await action()
      return true
    } catch (error) {
      toast.error(failure, { description: getErrorDescription(error) })
      return false
    }
  }

  return (
    <RunSheetSection
      id={id}
      number="02"
      title="Add-ons"
      summary={changed ? 'From the quote, changed for this event' : 'Ticked by the client on acceptance'}
      action={
        !isError && (
          <AddAddonPopover
            organizationId={event.organizationId}
            disabled={isSaving}
            onAdd={(catalogItemId, quantity) =>
              run(() => add.mutateAsync({ eventId: event.id, catalogItemId, quantity }), "Couldn't add the add-on")
            }
          />
        )
      }
    >
      {isError ? (
        <p className="text-sm text-muted-foreground">Couldn't load the add-ons.</p>
      ) : (
        <Skeleton
          data={addons}
          sample={SAMPLE_ADDONS}
          label="Loading add-ons"
          empty={addons?.length === 0 && removedAddons.length === 0}
          emptyMessage={<p className="text-sm text-muted-foreground">No add-ons on this event.</p>}
        >
          {(addons) => (
            <div className="flex flex-col gap-3">
              {addons.length === 0 ? (
                <p className="text-sm text-muted-foreground">No add-ons on this event.</p>
              ) : (
                <ul aria-label="Add-ons" className="divide-y">
                  {addons.map((addon) => (
                    <li key={addon.lineItemId || addon.addonOfferId} className="flex flex-wrap items-center gap-3 py-2 text-sm">
                      <span aria-hidden="true" className="size-2 shrink-0 rounded-full bg-status-success-fg" />
                      <span className="min-w-40 font-medium">{addon.name}</span>
                      {addon.source === EventAddonSourceEnum.EVENT_ADDON_SOURCE_EVENT && (
                        <Badge variant="outline">Added for this event</Badge>
                      )}
                      {addon.description && <span className="flex-1 text-muted-foreground">{addon.description}</span>}
                      <span className="ml-auto flex items-center gap-2">
                        {addonQuantity(addon) && (
                          <span className="font-financial text-xs text-muted-foreground">{addonQuantity(addon)}</span>
                        )}
                        {canEdit && (
                          <Button
                            type="button"
                            size="icon-sm"
                            variant="ghost"
                            aria-label={`Remove ${addon.name}`}
                            disabled={isSaving}
                            onClick={() =>
                              run(
                                () => remove.mutateAsync({ eventId: event.id, lineItemId: addon.lineItemId }),
                                "Couldn't remove the add-on",
                              )
                            }
                          >
                            <X />
                          </Button>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {removedAddons.length > 0 && (
                <div className="flex flex-col gap-1">
                  <p className="text-xs text-muted-foreground">Removed from this event. The quote still has them.</p>
                  <ul aria-label="Removed add-ons">
                    {removedAddons.map((addon) => (
                      <li key={addon.lineItemId} className="flex items-center gap-3 py-1 text-sm text-muted-foreground">
                        <span className="min-w-40 line-through">{addon.name}</span>
                        {canEdit && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="ml-auto"
                            aria-label={`Restore ${addon.name}`}
                            disabled={isSaving}
                            onClick={() =>
                              run(
                                () => restore.mutateAsync({ eventId: event.id, lineItemId: addon.lineItemId }),
                                "Couldn't restore the add-on",
                              )
                            }
                          >
                            <RotateCcw data-icon="inline-start" />
                            Restore
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </Skeleton>
      )}
    </RunSheetSection>
  )
}

interface AddAddonPopoverProps {
  organizationId: string
  disabled: boolean
  /** Resolves true once added; on false the failure was already reported. */
  onAdd: (catalogItemId: string, quantity: number) => Promise<boolean>
}

/** Picks a catalog add-on and how many, then adds it to the event. */
function AddAddonPopover({ organizationId, disabled, onAdd }: AddAddonPopoverProps) {
  const { items, isLoading } = useListItems(organizationId)
  const catalogAddons = items.filter((item) => item.offerAsAddon)
  const [open, setOpen] = useState(false)
  const [itemId, setItemId] = useState('')
  const [quantity, setQuantity] = useState('1')

  const amount = Number(quantity)
  const canSubmit = !!itemId && quantity !== '' && amount > 0 && amount <= MAX_ADDON_QUANTITY && !disabled

  const submit = async () => {
    if (!canSubmit) return
    if (!(await onAdd(itemId, amount))) return
    setOpen(false)
    setItemId('')
    setQuantity('1')
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <ActionGate organizationId={organizationId} permission="EventsManage" verb="add" noun="add-ons">
        <PopoverTrigger asChild>
          <Button type="button" size="sm" variant="ghost">
            <Plus data-icon="inline-start" />
            Add add-on
          </Button>
        </PopoverTrigger>
      </ActionGate>
      <PopoverContent align="end" className="w-72">
        {!isLoading && catalogAddons.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No catalog items are offered as add-ons. Turn on “Offer as add-on” for an item in the catalog.
          </p>
        ) : (
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault()
              void submit()
            }}
          >
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="add-addon-item">Add-on</Label>
              <Select value={itemId} onValueChange={setItemId}>
                <SelectTrigger id="add-addon-item" className="w-full">
                  <SelectValue placeholder={isLoading ? 'Loading…' : 'Choose an add-on'} />
                </SelectTrigger>
                <SelectContent>
                  {catalogAddons.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="add-addon-quantity">Quantity</Label>
              <Input
                id="add-addon-quantity"
                type="number"
                inputMode="decimal"
                min={0}
                max={MAX_ADDON_QUANTITY}
                step="any"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
            </div>
            <p className="text-xs text-muted-foreground">Changes this event only. The quote and its price stay the same.</p>
            <Button type="submit" size="sm" disabled={!canSubmit}>
              Add to event
            </Button>
          </form>
        )}
      </PopoverContent>
    </Popover>
  )
}

interface TimelineSectionProps {
  id: string
  event: Event
  onEdit: () => void
}

/**
 * 03 Timeline: the saved run of show. A block's start or duration can be
 * changed in place and the other follows; the event block itself stays where
 * the event details put it. Names, added blocks, order and Regenerate live in
 * the Timeline sheet. When the event's details changed since the blocks were
 * built, the notice says what.
 */
export function TimelineSection({ id, event, onEdit }: TimelineSectionProps) {
  const hasEventDate = !!event.eventDate
  const { timeline, isLoading, isError } = useEventTimeline(hasEventDate ? event.id : '')
  const { can } = usePermissions(event.organizationId)
  const { updateBlocks, isPending } = useUpdateEventTimelineBlocks()
  // The edited blocks while they save, so the row shows the new times at once.
  const [saving, setSaving] = useState<TimelineDraftBlock[] | null>(null)
  const timeZone = timeline?.timezone || event.eventTimezone || undefined

  const saveEdit = async (edit: TimelineEdit) => {
    if ('problem' in edit) {
      toast.error(edit.problem)
      return
    }
    setSaving(edit.draft)
    try {
      await updateBlocks({ eventId: event.id, blocks: draftToBlockInputs(edit.draft) })
    } catch (error) {
      toast.error('Could not save the timeline', { description: getErrorDescription(error) })
    } finally {
      setSaving(null)
    }
  }

  let summary: string | undefined
  if (timeline) {
    summary = timeline.isCustomized ? 'Edited by hand' : 'Built from the event details'
  }

  let body: ReactNode
  if (!hasEventDate) {
    body = <p className="text-sm text-muted-foreground">Set the event date to build a timeline.</p>
  } else if (!isLoading && (isError || !timeline)) {
    body = <p className="text-sm text-muted-foreground">Couldn't load the timeline.</p>
  } else {
    body = (
      <Skeleton data={isLoading ? null : timeline} sample={SAMPLE_TIMELINE} label="Loading timeline">
        {(timeline) => (
          <TimelineBlocks
            timeline={timeline}
            timeZone={timeZone}
            saving={saving}
            onEdit={!isLoading && can('EventsManage') ? saveEdit : undefined}
            disabled={isPending}
          />
        )}
      </Skeleton>
    )
  }

  return (
    <RunSheetSection
      id={id}
      number="03"
      title="Timeline"
      summary={summary}
      action={
        hasEventDate && (
          <ActionGate organizationId={event.organizationId} permission="EventsManage" verb="edit" noun="timelines">
            <Button type="button" size="sm" variant="ghost" onClick={onEdit}>
              <Pencil data-icon="inline-start" />
              Edit timeline
            </Button>
          </ActionGate>
        )
      }
    >
      {body}
    </RunSheetSection>
  )
}

interface TimelineBlocksProps {
  timeline: EventTimeline
  timeZone?: string
  /** Blocks being saved, shown in place of the saved ones until the save settles. */
  saving: TimelineDraftBlock[] | null
  /** Unset when the viewer can't edit timelines: the rows only read. */
  onEdit?: (edit: TimelineEdit) => void
  disabled: boolean
}

function TimelineBlocks({ timeline, timeZone, saving, onEdit, disabled }: TimelineBlocksProps) {
  const changes = describeTimelineChanges(timeline.changes)
  const rows = saving ?? draftFromBlocks(timeline.blocks)
  return (
    <div className="flex flex-col gap-3">
      {changes.length > 0 && (
        <p role="status" className="rounded-md border border-status-warning-border bg-status-warning-bg/60 px-3 py-2 text-xs text-status-warning-fg">
          Details changed since this timeline was built: {changes.join(', ')}. Open it to regenerate.
        </p>
      )}
      <ol aria-label="Timeline blocks">
        {rows.map((row, index) => {
          const dayOffset = timeline.blocks[index]?.dayOffset ?? 0
          const unknownDrive = isDriveKind(row.kind) && row.minutes === 0 && !timeline.driveTimeKnown
          const editable = onEdit ? inlineTimelineEdits(rows, index) : { start: false, minutes: false }
          const time = formatBlockTime(row.start, timeZone)
          const duration = unknownDrive ? 'Unknown' : formatBlockDuration(row.minutes)
          const name = row.label || `Block ${index + 1}`
          return (
            <li key={row.key} className="flex items-center gap-3 py-1.5 text-sm">
              <span aria-hidden="true" className={cn('h-5 w-0.5 shrink-0 rounded-full', timelineKindColor(row.kind))} />
              <span className="flex min-w-24 shrink-0 items-center font-financial text-xs">
                {onEdit && editable.start ? (
                  <InlineEditCell
                    label={`${name} start`}
                    type="time"
                    value={blockTimeInputValue(row.start, timeZone)}
                    disabled={disabled}
                    onCommit={(value) => onEdit(editDraftBlockStart(rows, index, withBlockTime(row.start, value, timeZone)))}
                    display={time}
                  />
                ) : (
                  time
                )}
                {dayOffset !== 0 && (
                  <span className="ml-1 text-muted-foreground">{dayOffset > 0 ? `+${dayOffset}d` : `${dayOffset}d`}</span>
                )}
              </span>
              <span className="min-w-0 flex-1">
                {row.label}
                {row.note && <span className="text-muted-foreground"> · {row.note}</span>}
              </span>
              <span className="text-xs text-muted-foreground">
                {onEdit && editable.minutes ? (
                  <InlineEditCell
                    label={`${name} minutes`}
                    type="number"
                    value={String(row.minutes)}
                    suffix="min"
                    disabled={disabled}
                    onCommit={(value) => onEdit(editDraftBlockMinutes(rows, index, value === '' ? Number.NaN : Number(value)))}
                    display={duration}
                  />
                ) : (
                  duration
                )}
              </span>
            </li>
          )
        })}
      </ol>
      {!timeline.driveTimeKnown && (
        <p className="text-xs text-muted-foreground">
          Drive time is unknown. Add the event location and your business address to estimate it.
        </p>
      )}
    </div>
  )
}

interface InlineEditCellProps {
  /** What the value is, e.g. "Setup start"; the button reads "Edit Setup start (2:30 PM)". */
  label: string
  type: 'time' | 'number'
  /** The input's value when editing starts. */
  value: string
  suffix?: string
  disabled: boolean
  onCommit: (value: string) => void
  /** The formatted value shown when not editing. */
  display: string
}

/**
 * Click to edit, Enter or blur to commit, Escape to cancel, the same as the
 * job page's inline cells. An unchanged value commits nothing.
 */
function InlineEditCell({ label, type, value, suffix, disabled, onCommit, display }: InlineEditCellProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const cancelledRef = useRef(false)

  function commit() {
    setIsEditing(false)
    if (draft !== value) onCommit(draft)
  }
  function handleBlur() {
    if (cancelledRef.current) {
      cancelledRef.current = false
      return
    }
    commit()
  }
  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault()
      commit()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancelledRef.current = true
      setIsEditing(false)
    }
  }

  if (isEditing) {
    return (
      <span className="flex items-center gap-1">
        <Input
          autoFocus
          type={type}
          aria-label={label}
          value={draft}
          {...(type === 'number' && { inputMode: 'numeric' as const, min: 0, max: MAX_TIMELINE_BLOCK_MINUTES })}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={cn('h-7', type === 'time' ? 'w-28' : 'w-20')}
        />
        {suffix}
      </span>
    )
  }

  return (
    <button
      type="button"
      aria-label={`Edit ${label} (${display})`}
      disabled={disabled}
      onClick={() => {
        setDraft(value)
        setIsEditing(true)
      }}
      className={cn(
        '-mx-1.5 rounded px-1.5 py-0.5 outline-none transition-colors',
        'hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring/50',
        'disabled:cursor-default disabled:opacity-60 disabled:hover:bg-transparent',
      )}
    >
      {display}
    </button>
  )
}

interface OnSiteSectionProps {
  id: string
  event: Event
  /** The event's booking; undefined while it loads. */
  booking: Booking | undefined
  onEdit: () => void
}

/**
 * 05 On-site details: the booking questionnaire's questions with the client's
 * answers, plus the on-site contact. Questions are edited in the questionnaire
 * sheet.
 */
export function OnSiteSection({ id, event, booking, onEdit }: OnSiteSectionProps) {
  const items = booking ? sortedQuestionnaireItems(booking.questionnaire) : []
  const delivery = booking ? questionnaireDeliveryLabel(booking.questionnaireSentAt, booking.questionnaireReturnedAt) : undefined
  const contact = event.contact
  const contactName = contact ? formatFullName(contact.firstName, contact.lastName, '') : ''
  const contactLine = [contactName, contact?.phoneNumber].filter(Boolean).join(' · ')

  return (
    <RunSheetSection
      id={id}
      number="05"
      title="On-site details"
      summary={booking ? ['From the client questionnaire', delivery].filter(Boolean).join(' · ') : undefined}
      action={
        booking && (
          <ActionGate organizationId={booking.organizationId} permission="EventsManage" verb="edit" noun="the questionnaire">
            <Button type="button" size="sm" variant={items.length > 0 ? 'ghost' : 'outline'} onClick={onEdit}>
              {items.length > 0 ? <Pencil data-icon="inline-start" /> : <Plus data-icon="inline-start" />}
              {items.length > 0 ? 'Edit questions' : 'Add questions'}
            </Button>
          </ActionGate>
        )
      }
    >
      <Skeleton data={booking} sample={SAMPLE_BOOKING} label="Loading questionnaire">
        {(booking) => <QuestionnaireAnswers booking={booking} contactLine={contactLine} />}
      </Skeleton>
    </RunSheetSection>
  )
}

function QuestionnaireAnswers({ booking, contactLine }: { booking: Booking; contactLine: string }) {
  const items = sortedQuestionnaireItems(booking.questionnaire)
  return (
    <dl className="grid gap-x-7 gap-y-3 sm:grid-cols-2">
      {items.length === 0 && (
        <p className="text-sm text-muted-foreground sm:col-span-2">No questions on this booking yet.</p>
      )}
      {items.map((item) => {
        const answer = formatQuestionnaireAnswer(item)
        return (
          <div key={item.id} className="flex flex-col gap-0.5">
            <dt className="text-xs text-muted-foreground">{item.prompt}</dt>
            <dd className={cn('text-sm', !answer && 'text-muted-foreground italic')}>{answer || 'No answer yet'}</dd>
          </div>
        )
      })}
      {contactLine && (
        <div className="flex flex-col gap-0.5">
          <dt className="text-xs text-muted-foreground">On-site contact</dt>
          <dd className="text-sm">{contactLine}</dd>
        </div>
      )}
    </dl>
  )
}
